import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Table, InputNumber, Input, Button, Tag, Tooltip, Popconfirm, Empty, message, Alert } from 'antd';
import { ExportOutlined, UndoOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { cuttingPlannerApi } from './cuttingPlannerApi';
import { formatThickness, stockDisplayName } from './cuttingPlannerHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage CUTTING-PLANNER-STOCK-ISSUE — "Issue to production" panel.
//
// The operator MANUALLY picks an on-hand stock row and enters the number of
// SHEETS consumed by this saved plan. The server deducts sheets from qty_balance
// and sheets × per-sheet area from the area balances (hard-blocked when short),
// and every issue is reversible. Deduction is never automatic — this dialog is
// the only trigger.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  const e = err && err.response && err.response.data && err.response.data.error;
  if (e && e.message) {
    if (e.details && e.details.availableSheets != null) {
      return `${e.message} (need ${e.details.requestedSheets}, ${e.details.availableSheets} on hand).`;
    }
    return e.message;
  }
  return (err && err.message) || fallback || 'Something went wrong.';
}

const n3 = (v) => (v == null ? '—' : Number(v).toFixed(3));
const sheetSize = (r) => (r.widthMm && r.heightMm ? `${Math.round(r.widthMm)} × ${Math.round(r.heightMm)}` : '—');

export default function StockIssueModal({ open, planId, planNo, canIssue, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sheets, setSheets] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [reversingId, setReversingId] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try { setData(await cuttingPlannerApi.availableStock(planId)); }
    catch (err) { message.error(backendError(err, 'Could not load stock.')); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => {
    if (open) { setSelectedId(null); setSheets(null); setRemarks(''); setDirty(false); load(); }
  }, [open, load]);

  const candidates = (data && data.candidates) || [];
  const issues = (data && data.issues) || [];
  const planStocks = (data && data.planStocks) || [];
  // A reversal keeps the issue row as an audit record (status REVERSED) — count
  // only the still-active ones so the header reflects real consumption.
  const activeIssueCount = issues.filter((i) => i.status !== 'REVERSED').length;
  const reversedIssueCount = issues.length - activeIssueCount;
  const selected = useMemo(() => candidates.find((c) => c.currentStockId === selectedId) || null, [candidates, selectedId]);

  // Live preview of the deduction against the picked row.
  const preview = useMemo(() => {
    if (!selected || !(sheets > 0)) return null;
    const dSqmt = sheets * (selected.perSheetSqmt || 0);
    return {
      dSqmt,
      remainingQty: (selected.qtyBalance || 0) - sheets,
      remainingSqmt: (selected.sqmtBalance || 0) - dSqmt,
      short: sheets > (selected.qtyBalance || 0) + 1e-6
    };
  }, [selected, sheets]);

  const doIssue = useCallback(async () => {
    if (!selected || !(sheets > 0)) return;
    setBusy(true);
    try {
      const out = await cuttingPlannerApi.issueStock(planId, {
        current_stock_id: selected.currentStockId, sheets, remarks: remarks || undefined
      });
      message.success(`Issued ${out.sheets} sheet${out.sheets === 1 ? '' : 's'} · ${n3(out.sqmt)} sqmt.`);
      setSheets(null); setRemarks(''); setSelectedId(null); setDirty(true);
      load();
    } catch (err) { message.error(backendError(err, 'Could not issue stock.')); }
    finally { setBusy(false); }
  }, [selected, sheets, remarks, planId, load]);

  const doReverse = useCallback(async (issueId) => {
    setReversingId(issueId);
    try {
      await cuttingPlannerApi.reverseIssue(planId, issueId);
      message.success('Issue reversed — stock restored.');
      setDirty(true); load();
    } catch (err) { message.error(backendError(err, 'Could not reverse the issue.')); }
    finally { setReversingId(null); }
  }, [planId, load]);

  const handleClose = useCallback(() => { if (dirty && onChanged) onChanged(); onClose && onClose(); }, [dirty, onChanged, onClose]);

  const candColumns = [
    { title: 'Stock item', key: 'item', ellipsis: true,
      render: (_v, r) => (
        <div className="leading-tight">
          <div className="text-[12px] font-medium text-ink-800 truncate" title={r.itemName || ''}>{stockDisplayName(r.itemName) || '—'}</div>
          <div className="text-[11px] text-ink-400 truncate">
            {r.warehouseName ? `${r.warehouseName}` : 'No warehouse'}{r.batchNo ? ` · ${r.batchNo}` : ''}
          </div>
        </div>
      ) },
    { title: 'Thk', dataIndex: 'thicknessMm', key: 'thk', width: 54, align: 'center',
      render: (v) => (v == null ? '—' : formatThickness(v)) },
    { title: 'Sheet (mm)', key: 'size', width: 108, align: 'right', render: (_v, r) => <span className="tabular-nums text-[12px]">{sheetSize(r)}</span> },
    { title: 'On hand', key: 'qty', width: 78, align: 'right',
      render: (_v, r) => <span className="tabular-nums font-semibold text-emerald-600">{n3(r.qtyBalance)}</span> },
    { title: 'sqmt', dataIndex: 'sqmtBalance', key: 'sqmt', width: 84, align: 'right', render: (v) => <span className="tabular-nums text-[12px] text-ink-500">{n3(v)}</span> },
    { title: 'Per sheet', dataIndex: 'perSheetSqmt', key: 'per', width: 84, align: 'right', render: (v) => <span className="tabular-nums text-[12px] text-ink-400">{n3(v)} m²</span> }
  ];

  const issueColumns = [
    { title: 'Stock item', key: 'item', ellipsis: true,
      render: (_v, r) => <span className="text-[12px]" title={r.itemName || ''}>{stockDisplayName(r.itemName) || '—'}</span> },
    { title: 'Sheets', dataIndex: 'sheets', key: 'sheets', width: 72, align: 'right',
      render: (v, r) => <span className={`tabular-nums font-semibold${r.status === 'REVERSED' ? ' line-through' : ''}`}>{n3(v)}</span> },
    { title: 'sqmt', dataIndex: 'sqmt', key: 'sqmt', width: 84, align: 'right',
      render: (v, r) => <span className={`tabular-nums text-ink-500${r.status === 'REVERSED' ? ' line-through' : ''}`}>{n3(v)}</span> },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 92, align: 'center',
      render: (v) => v === 'REVERSED' ? <Tag className="!m-0">Reversed</Tag> : <Tag color="blue" className="!m-0">Issued</Tag> },
    { title: 'When', dataIndex: 'createdAt', key: 'when', width: 118, render: (v) => v ? dayjs(v).format('DD MMM HH:mm') : '—' },
    { title: '', key: 'x', width: 96, align: 'center',
      render: (_v, r) => r.status === 'ISSUED' && canIssue ? (
        <Popconfirm title="Reverse this issue?" description="The sheets + area go back to on-hand stock." okText="Reverse" onConfirm={() => doReverse(r.id)}>
          <Button size="small" type="text" icon={<UndoOutlined />} loading={reversingId === r.id}>Reverse</Button>
        </Popconfirm>
      ) : null }
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={880}
      title={<span className="text-[15px] font-semibold text-ink-800">Issue stock to production · <span className="text-brand-600">{planNo || ''}</span></span>}
      footer={<Button onClick={handleClose}>Close</Button>}
      destroyOnClose
    >
      {planStocks.length ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mr-1">Plan uses</span>
          {planStocks.map((s, i) => (
            <Tag key={i} className="!m-0 !text-[11px]">
              {stockDisplayName(s.stockName) || '—'}{s.thickness != null ? ` · ${formatThickness(s.thickness)}mm` : ''} · {s.pieces} pc · {n3(s.netSqmt)} m²
            </Tag>
          ))}
        </div>
      ) : null}

      {!canIssue ? (
        <Alert type="info" showIcon className="!mb-3" message="You have read-only access — issuing stock needs the stock-issue permission." />
      ) : null}

      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 mb-1.5">1 · Pick the on-hand stock to consume</div>
      <Table
        className="cutting-grid" size="small" rowKey="currentStockId" loading={loading}
        columns={candColumns} dataSource={candidates}
        pagination={false} scroll={{ y: 220 }}
        rowSelection={canIssue ? { type: 'radio', selectedRowKeys: selectedId ? [selectedId] : [], onChange: (keys) => setSelectedId(keys[0]) } : undefined}
        onRow={(r) => ({ onClick: () => canIssue && setSelectedId(r.currentStockId) })}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No on-hand glass stock. Post a GRN inward first." /> }}
      />

      <div className="mt-4 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 flex flex-wrap items-end gap-x-5 gap-y-3">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">2 · Sheets used</span>
          <InputNumber min={1} step={1} precision={0} value={sheets} onChange={setSheets} disabled={!canIssue || !selected}
            style={{ width: 120 }} placeholder="0" />
        </div>
        <div className="flex flex-col gap-1 min-w-[150px]">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Remarks (optional)</span>
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canIssue || !selected} maxLength={500} placeholder="e.g. batch A" />
        </div>
        {preview ? (
          <div className="flex flex-col leading-tight text-[12px]">
            <span className="text-ink-500">Deduct <b className="text-ink-800 tabular-nums">{n3(preview.dSqmt)} m²</b></span>
            <span className={preview.short ? 'text-red-600' : 'text-ink-500'}>
              Remaining <b className="tabular-nums">{n3(preview.remainingQty)}</b> sheets · <b className="tabular-nums">{n3(preview.remainingSqmt)}</b> m²
            </span>
          </div>
        ) : null}
        <div className="flex-1" />
        <Tooltip title={selected ? '' : 'Pick a stock row first'}>
          <Button type="primary" icon={<ExportOutlined />} onClick={doIssue} loading={busy}
            disabled={busy || !canIssue || !selected || !(sheets > 0) || (preview && preview.short)}>
            Issue{sheets > 0 ? ` ${sheets} sheet${sheets === 1 ? '' : 's'}` : ''}
          </Button>
        </Tooltip>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <InboxOutlined className="text-brand-500 text-[14px]" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Issued from this plan</span>
        {activeIssueCount ? <Tag color="blue" className="!m-0 tabular-nums">{activeIssueCount} active</Tag> : null}
        {reversedIssueCount ? <span className="text-[11px] text-ink-400 tabular-nums">{reversedIssueCount} reversed</span> : null}
      </div>
      <Table
        className="cutting-grid mt-1.5" size="small" rowKey="id" columns={issueColumns} dataSource={issues}
        pagination={false} scroll={{ y: 180 }}
        onRow={(r) => (r.status === 'REVERSED' ? { style: { opacity: 0.5 } } : {})}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing issued yet." /> }}
      />
    </Modal>
  );
}
